// src/components/ui/NotificationPanel.js  — Enterprise redesign
import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BellIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { BellIcon as BellSolid } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../context/NotificationContext";

// ─── Color helpers ────────────────────────────────────────────────────────────
const hexToRgb = (hex) => {
  const h = (hex || "#26256b").replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16) || 0,
    g: parseInt(h.slice(2, 4), 16) || 0,
    b: parseInt(h.slice(4, 6), 16) || 0,
  };
};
const rgba = (hex, a) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
};
// Blend toward black (factor 0=original, 1=black)
const shade = (hex, f) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.round(r * (1 - f))},${Math.round(g * (1 - f))},${Math.round(b * (1 - f))})`;
};
// Blend toward white (factor 0=original, 1=white)
const tint = (hex, f) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.round(r + (255 - r) * f)},${Math.round(g + (255 - g) * f)},${Math.round(b + (255 - b) * f)})`;
};

// ─── Type theme map (all inline – no Tailwind purge risk) ─────────────────────
const TYPE_CONFIG = {
  success: {
    icon: CheckCircleIcon,
    accent: "#10B981",
    tileBg: "#ECFDF5",
    tileBgDark: "rgba(6,78,59,0.25)",
    iconColor: "#059669",
    dotColor: "#10B981",
    badgeBg: "#D1FAE5",
    badgeText: "#065F46",
    label: "Resolved",
  },
  warning: {
    icon: ExclamationTriangleIcon,
    accent: "#F59E0B",
    tileBg: "#FFFBEB",
    tileBgDark: "rgba(120,53,15,0.25)",
    iconColor: "#D97706",
    dotColor: "#F59E0B",
    badgeBg: "#FEF3C7",
    badgeText: "#92400E",
    label: "Warning",
  },
  error: {
    icon: ShieldExclamationIcon,
    accent: "#EF4444",
    tileBg: "#FFF1F2",
    tileBgDark: "rgba(127,29,29,0.25)",
    iconColor: "#DC2626",
    dotColor: "#EF4444",
    badgeBg: "#FEE2E2",
    badgeText: "#991B1B",
    label: "Critical",
  },
  info: {
    icon: InformationCircleIcon,
    accent: "#3B82F6",
    tileBg: "#EFF6FF",
    tileBgDark: "rgba(30,58,138,0.25)",
    iconColor: "#2563EB",
    dotColor: "#3B82F6",
    badgeBg: "#DBEAFE",
    badgeText: "#1D4ED8",
    label: "Info",
  },
};

const MODULE_CONFIG = {
  incident_reporting: { label: "Incident",   color: "#7C3AED", bg: "#F5F3FF" },
  risk_management:    { label: "Risk",        color: "#DC2626", bg: "#FEF2F2" },
  compliance:         { label: "Compliance",  color: "#0891B2", bg: "#ECFEFF" },
  system:             { label: "System",      color: "#6B7280", bg: "#F9FAFB" },
};

const getTypeConfig   = (type)   => TYPE_CONFIG[type]    || TYPE_CONFIG.info;
const getModuleConfig = (module) => MODULE_CONFIG[module] || { label: module || "System", color: "#6B7280", bg: "#F9FAFB" };

// ─── Utilities ────────────────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)     return "just now";
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const dayLabel = (dateStr) => {
  if (!dateStr) return "Earlier";
  const d = new Date(dateStr);
  if (isNaN(d)) return "Earlier";
  const now       = new Date();
  const today     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today - 86_400_000);
  const nd        = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (nd >= today)     return "Today";
  if (nd >= yesterday) return "Yesterday";
  return "Earlier";
};

const groupByDay = (items) => {
  const map = {};
  items.forEach((n) => {
    const k = dayLabel(n.created_at);
    if (!map[k]) map[k] = [];
    map[k].push(n);
  });
  return ["Today", "Yesterday", "Earlier"]
    .filter((k) => map[k])
    .map((k) => ({ label: k, items: map[k] }));
};

// ─── Skeleton loading rows ────────────────────────────────────────────────────
const SkeletonRow = ({ isDarkMode }) => {
  const base  = isDarkMode ? "#0F172A" : "#FFFFFF";
  const track = isDarkMode ? "#1E293B" : "#F1F5F9";
  const flash = isDarkMode ? "#2D3F55" : "#E8EDF2";
  const shimmer = `linear-gradient(90deg,${track} 25%,${flash} 50%,${track} 75%)`;
  return (
    <div style={{ display: "flex", gap: 12, padding: "14px 20px", background: base, alignItems: "flex-start" }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: shimmer, backgroundSize: "200% 100%", animation: "sk-shimmer 1.4s ease-in-out infinite" }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 11, borderRadius: 6, width: "75%", background: shimmer, backgroundSize: "200% 100%", animation: "sk-shimmer 1.4s ease-in-out infinite" }} />
        <div style={{ height: 9,  borderRadius: 6, width: "48%", background: shimmer, backgroundSize: "200% 100%", animation: "sk-shimmer 1.4s ease-in-out 0.12s infinite" }} />
      </div>
      <style>{`@keyframes sk-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </div>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState = ({ isDarkMode }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "52px 24px", gap: 18 }}>
    <div style={{
      width: 76, height: 76, borderRadius: 22,
      background: isDarkMode ? "#0D1B2E" : "#F0F4FF",
      border: `2px dashed ${isDarkMode ? "#1E293B" : "#C7D7FF"}`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <BellSolid style={{ width: 30, height: 30, color: isDarkMode ? "#1E3A5F" : "#BFCFEF" }} />
    </div>
    <div style={{ textAlign: "center" }}>
      <p style={{ fontSize: 14, fontWeight: 700, color: isDarkMode ? "#CBD5E1" : "#1E293B", margin: 0, marginBottom: 8, letterSpacing: "-0.01em" }}>
        You're all caught up
      </p>
      <p style={{ fontSize: 12, color: isDarkMode ? "#475569" : "#94A3B8", margin: 0, lineHeight: 1.65, maxWidth: 230 }}>
        Security alerts, incident reports and status updates will appear here in real time.
      </p>
    </div>
  </div>
);

// ─── Header icon button ───────────────────────────────────────────────────────
const HeaderBtn = ({ onClick, title, isDarkMode, icon, label, danger, primaryColor = "#26256b" }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        height: 30, padding: label ? "0 10px" : "0 8px",
        borderRadius: 8, border: "none", cursor: "pointer",
        background: hov
          ? (danger ? (isDarkMode ? "rgba(239,68,68,0.18)" : "#FEE2E2") : (isDarkMode ? shade(primaryColor, 0.12) : rgba(primaryColor, 0.08)))
          : (isDarkMode ? shade(primaryColor, 0.25) : rgba(primaryColor, 0.06)),
        color: hov
          ? (danger ? "#EF4444" : (isDarkMode ? tint(primaryColor, 0.5) : primaryColor))
          : (isDarkMode ? tint(primaryColor, 0.25) : "#6B7280"),
        fontSize: 11, fontWeight: 600, transition: "all 0.15s",
        boxShadow: hov ? "none" : `0 0 0 1px ${isDarkMode ? shade(primaryColor, 0.12) : rgba(primaryColor, 0.15)}`,
      }}
    >
      {icon}
      {label && <span>{label}</span>}
    </button>
  );
};

// ─── Main panel ───────────────────────────────────────────────────────────────
const NotificationPanel = ({ isDarkMode, primaryColor = "#26256b" }) => {
  const {
    notifications, unreadCount, loading, panelOpen, pagination,
    closePanel, markRead, markAllRead, deleteNotification, clearAll, loadMore,
  } = useNotifications();

  const [activeTab, setActiveTab] = useState("all");
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const handleNotificationClick = (n) => {
    if (!n.is_read) markRead(n.id);
    closePanel();
    if (n.reference_url) navigate(n.reference_url);
  };

  useEffect(() => {
    if (!panelOpen) return;
    const h = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) closePanel(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [panelOpen, closePanel]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") closePanel(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [closePanel]);

  const filtered = activeTab === "unread" ? notifications.filter((n) => !n.is_read) : notifications;
  const groups   = groupByDay(filtered);

  const T = {
    panelBg:     isDarkMode ? shade(primaryColor, 0.55) : "#FFFFFF",
    headerBg:    isDarkMode ? shade(primaryColor, 0.25) : "#F7F7FF",
    bodyBg:      isDarkMode ? shade(primaryColor, 0.55) : "#FFFFFF",
    border:      isDarkMode ? shade(primaryColor, 0.12) : "#ECEAF8",
    sectionBg:   isDarkMode ? shade(primaryColor, 0.25) : "#F4F3FB",
    sectionText: isDarkMode ? tint(primaryColor, 0.25) : "#9491C4",
    footerBg:    isDarkMode ? shade(primaryColor, 0.25) : "#F7F7FF",
    footerText:  isDarkMode ? tint(primaryColor, 0.25) : "#9491C4",
    tabActive:   primaryColor,
    tabInact:    isDarkMode ? tint(primaryColor, 0.25) : "#9491C4",
    tabActiveBg: isDarkMode ? rgba(primaryColor, 0.25) : rgba(primaryColor, 0.08),
    shadow: isDarkMode
      ? `0 0 0 1px ${shade(primaryColor, 0.12)}, 0 8px 16px rgba(0,0,0,0.4), 0 32px 80px rgba(0,0,0,0.7)`
      : `0 0 0 1px ${rgba(primaryColor, 0.1)}, 0 4px 12px rgba(0,0,0,0.05), 0 24px 64px ${rgba(primaryColor, 0.1)}`,
  };

  return (
    <AnimatePresence>
      {panelOpen && (
        <>
          {/* Scrim */}
          <motion.div
            key="notif-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(10,15,30,0.45)", backdropFilter: "blur(3px)" }}
          />

          {/* Panel */}
          <motion.div
            key="notif-panel"
            ref={panelRef}
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 440, damping: 36 }}
            style={{
              position: "fixed", top: 62, right: 16, zIndex: 50,
              width: 420, maxHeight: "calc(100vh - 82px)",
              display: "flex", flexDirection: "column",
              borderRadius: 16, overflow: "hidden",
              background: T.panelBg,
              border: `1px solid ${T.border}`,
              boxShadow: T.shadow,
            }}
          >

            {/* ── Header ─────────────────────────────────────────────── */}
            <div style={{ background: T.headerBg, borderBottom: `1px solid ${T.border}`, padding: "16px 20px 0", flexShrink: 0 }}>

              {/* Top row */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>

                {/* Left: bell + title */}
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 13,
                      background: `linear-gradient(140deg,${tint(primaryColor, 0.3)} 0%,${primaryColor} 100%)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: `0 4px 14px ${rgba(primaryColor, 0.45)}, inset 0 1px 0 rgba(255,255,255,0.15)`,
                    }}>
                      <BellIcon style={{ width: 19, height: 19, color: "#fff" }} />
                    </div>
                    {unreadCount > 0 && (
                      <span style={{
                        position: "absolute", top: -4, right: -4,
                        minWidth: 19, height: 19, borderRadius: 10,
                        background: "linear-gradient(135deg,#F87171,#DC2626)",
                        border: `2px solid ${T.headerBg}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, fontWeight: 800, color: "#fff", padding: "0 4px", lineHeight: 1,
                        boxShadow: "0 2px 6px rgba(220,38,38,0.5)",
                      }}>
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.02em", color: isDarkMode ? "#E2E8F0" : "#0F172A", margin: 0 }}>
                      Notifications
                    </p>
                    <p style={{ fontSize: 11, color: T.sectionText, margin: 0, marginTop: 2 }}>
                      {unreadCount > 0 ? `${unreadCount} new alert${unreadCount !== 1 ? "s" : ""}` : "All caught up"}
                    </p>
                  </div>
                </div>

                {/* Right: actions */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {unreadCount > 0 && (
                    <HeaderBtn
                      onClick={markAllRead}
                      title="Mark all as read"
                      isDarkMode={isDarkMode}
                      primaryColor={primaryColor}
                      icon={<CheckIcon style={{ width: 13, height: 13 }} />}
                      label="All read"
                    />
                  )}
                  {notifications.length > 0 && (
                    <HeaderBtn
                      onClick={clearAll}
                      title="Clear all"
                      isDarkMode={isDarkMode}
                      primaryColor={primaryColor}
                      danger
                      icon={<TrashIcon style={{ width: 13, height: 13 }} />}
                    />
                  )}
                  <button
                    onClick={closePanel}
                    title="Close"
                    style={{
                      width: 30, height: 30, borderRadius: 8, border: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: isDarkMode ? shade(primaryColor, 0.25) : rgba(primaryColor, 0.06),
                      color: isDarkMode ? tint(primaryColor, 0.25) : "#9491C4",
                      boxShadow: `0 0 0 1px ${T.border}`,
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = isDarkMode ? shade(primaryColor, 0.12) : rgba(primaryColor, 0.08);
                      e.currentTarget.style.color = isDarkMode ? tint(primaryColor, 0.5) : primaryColor;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isDarkMode ? shade(primaryColor, 0.25) : rgba(primaryColor, 0.06);
                      e.currentTarget.style.color = isDarkMode ? tint(primaryColor, 0.25) : "#9491C4";
                    }}
                  >
                    <XMarkIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>

              {/* Tab row */}
              <div style={{ display: "flex", gap: 2 }}>
                {[
                  { id: "all",    label: "All",    count: notifications.length },
                  { id: "unread", label: "Unread", count: unreadCount },
                ].map((tab) => {
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 7,
                        padding: "8px 14px",
                        border: "none", borderRadius: "8px 8px 0 0",
                        cursor: "pointer",
                        background: active ? T.tabActiveBg : "transparent",
                        color: active ? T.tabActive : T.tabInact,
                        fontSize: 12, fontWeight: active ? 700 : 500,
                        transition: "all 0.15s",
                        borderBottom: `2px solid ${active ? T.tabActive : "transparent"}`,
                        letterSpacing: active ? "-0.01em" : "0",
                      }}
                    >
                      {tab.label}
                      {tab.count > 0 && (
                        <span style={{
                          minWidth: 19, height: 19, borderRadius: 10, padding: "0 5px",
                          background: active ? T.tabActive : (isDarkMode ? shade(primaryColor, 0.12) : rgba(primaryColor, 0.12)),
                          color: active ? "#fff" : (isDarkMode ? tint(primaryColor, 0.25) : "#6B7280"),
                          fontSize: 10, fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          lineHeight: 1,
                          transition: "all 0.15s",
                        }}>
                          {tab.count > 99 ? "99+" : tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Body ───────────────────────────────────────────────── */}
            <div style={{ flex: 1, overflowY: "auto", background: T.bodyBg }}>
              {loading && notifications.length === 0 ? (
                <>
                  <SkeletonRow isDarkMode={isDarkMode} />
                  <SkeletonRow isDarkMode={isDarkMode} />
                  <SkeletonRow isDarkMode={isDarkMode} />
                </>
              ) : groups.length === 0 ? (
                <EmptyState isDarkMode={isDarkMode} filtered={activeTab === "unread"} />
              ) : (
                <div>
                  {groups.map(({ label, items }, gi) => (
                    <div key={label}>
                      {/* Section divider */}
                      <div style={{
                        position: "sticky", top: 0, zIndex: 10,
                        background: T.sectionBg,
                        borderBottom: `1px solid ${T.border}`,
                        borderTop: gi > 0 ? `1px solid ${T.border}` : "none",
                        padding: "6px 20px",
                        display: "flex", alignItems: "center", gap: 10,
                      }}>
                        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: T.sectionText }}>
                          {label}
                        </span>
                        <div style={{ flex: 1, height: 1, background: T.border }} />
                        <span style={{
                          fontSize: 10, fontWeight: 600, color: T.sectionText,
                          background: isDarkMode ? shade(primaryColor, 0.35) : "#DDE3F0",
                          padding: "1px 6px", borderRadius: 8,
                        }}>
                          {items.length}
                        </span>
                      </div>

                      {items.map((n, idx) => (
                        <NotificationItem
                          key={n.id}
                          notification={n}
                          isDarkMode={isDarkMode}
                          primaryColor={primaryColor}
                          index={idx}
                          border={T.border}
                          onClick={() => handleNotificationClick(n)}
                          onDelete={() => deleteNotification(n.id)}
                        />
                      ))}
                    </div>
                  ))}

                  {pagination.has_next && (
                    <div style={{ padding: "14px 20px", textAlign: "center", borderTop: `1px solid ${T.border}` }}>
                      <button
                        onClick={loadMore}
                        disabled={loading}
                        style={{
                          background: isDarkMode ? shade(primaryColor, 0.25) : rgba(primaryColor, 0.06),
                          border: `1px solid ${isDarkMode ? shade(primaryColor, 0.12) : rgba(primaryColor, 0.3)}`,
                          borderRadius: 9, padding: "8px 22px",
                          fontSize: 12, fontWeight: 600,
                          color: isDarkMode ? tint(primaryColor, 0.5) : primaryColor,
                          cursor: loading ? "not-allowed" : "pointer",
                          opacity: loading ? 0.6 : 1,
                          transition: "all 0.15s",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {loading ? "Loading…" : "Show older notifications"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Footer ─────────────────────────────────────────────── */}
            {filtered.length > 0 && (
              <div style={{
                background: T.footerBg,
                borderTop: `1px solid ${T.border}`,
                padding: "10px 20px",
                flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span style={{ fontSize: 11, color: T.footerText }}>
                  {filtered.length} of {pagination.total_count ?? filtered.length}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: T.footerText }}>
                  <FunnelIcon style={{ width: 11, height: 11 }} />
                  {activeTab === "unread" ? "Unread only" : "All notifications"}
                </span>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ─── Notification item ────────────────────────────────────────────────────────
const NotificationItem = ({ notification: n, isDarkMode, primaryColor = "#26256b", onClick, onDelete, index, border }) => {
  const [hov, setHov] = useState(false);
  const cfg  = getTypeConfig(n.notification_type);
  const mod  = getModuleConfig(n.module);
  const Icon = cfg.icon;

  const unreadBg  = isDarkMode ? rgba(primaryColor, 0.18) : rgba(primaryColor, 0.06);
  const hoverBg   = isDarkMode ? "rgba(255,255,255,0.025)" : rgba(primaryColor, 0.04);
  const defaultBg = "transparent";
  const rowBg     = hov ? hoverBg : (!n.is_read ? unreadBg : defaultBg);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 14, transition: { duration: 0.14 } }}
      transition={{ duration: 0.18, delay: index * 0.025 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        position: "relative",
        display: "flex", gap: 13,
        padding: "13px 20px 13px 17px",
        cursor: "pointer",
        background: rowBg,
        borderBottom: `1px solid ${border}`,
        transition: "background 0.15s",
        borderLeft: `3px solid ${!n.is_read ? cfg.accent : "transparent"}`,
      }}
    >
      {/* Icon tile */}
      <div style={{
        flexShrink: 0, width: 40, height: 40, borderRadius: 11,
        background: isDarkMode ? cfg.tileBgDark : cfg.tileBg,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `0 0 0 1px ${isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.05)"}`,
      }}>
        <Icon style={{ width: 19, height: 19, color: cfg.iconColor }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, marginBottom: 6,
          fontSize: 12.5, lineHeight: 1.55,
          letterSpacing: "-0.01em",
          color: isDarkMode ? (n.is_read ? "#4B6080" : "#DDE6F5") : (n.is_read ? "#64748B" : "#0F172A"),
          fontWeight: n.is_read ? 400 : 650,
        }}>
          {n.message}
        </p>

        {/* Meta row */}
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5 }}>
          {/* Module chip */}
          <span style={{
            fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
            padding: "2px 7px", borderRadius: 5,
            background: isDarkMode ? "rgba(255,255,255,0.06)" : mod.bg,
            color: isDarkMode ? "#7A9EC0" : mod.color,
          }}>
            {mod.label}
          </span>

          {/* Severity chip */}
          <span style={{
            fontSize: 9.5, fontWeight: 600, letterSpacing: "0.03em",
            padding: "2px 7px", borderRadius: 5,
            background: isDarkMode ? "rgba(255,255,255,0.05)" : cfg.badgeBg,
            color: isDarkMode ? cfg.iconColor : cfg.badgeText,
          }}>
            {cfg.label}
          </span>

          <span style={{ width: 3, height: 3, borderRadius: "50%", background: isDarkMode ? rgba(primaryColor, 0.4) : "#D1D5DB", flexShrink: 0 }} />

          <span style={{ fontSize: 11, color: isDarkMode ? tint(primaryColor, 0.25) : "#9CA3AF" }}>
            {timeAgo(n.created_at)}
          </span>

          {n.from_user_name && (
            <>
              <span style={{ fontSize: 11, color: isDarkMode ? rgba(primaryColor, 0.4) : "#D1D5DB" }}>·</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: isDarkMode ? "#4B6080" : "#64748B" }}>
                {n.from_user_name}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Unread dot */}
      {!n.is_read && (
        <div style={{
          flexShrink: 0, width: 8, height: 8, borderRadius: "50%",
          background: cfg.dotColor, marginTop: 16, alignSelf: "flex-start",
          boxShadow: `0 0 0 3px ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.9)"}, 0 0 10px ${cfg.dotColor}55`,
        }} />
      )}

      {/* Hover delete button */}
      <AnimatePresence>
        {hov && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.12 }}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Dismiss"
            style={{
              position: "absolute", top: 10, right: 12,
              width: 26, height: 26, borderRadius: 8,
              border: "none", cursor: "pointer",
              background: isDarkMode ? shade(primaryColor, 0.65) : "#EEF2F9",
              color: isDarkMode ? tint(primaryColor, 0.25) : "#94A3B8",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 0 0 1px ${isDarkMode ? shade(primaryColor, 0.35) : "#DDE3F0"}`,
              transition: "all 0.12s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.boxShadow = "0 0 0 1px #FCA5A5"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = isDarkMode ? shade(primaryColor, 0.65) : "#EEF2F9"; e.currentTarget.style.color = isDarkMode ? tint(primaryColor, 0.25) : "#94A3B8"; e.currentTarget.style.boxShadow = `0 0 0 1px ${isDarkMode ? shade(primaryColor, 0.35) : "#DDE3F0"}`; }}
          >
            <XMarkIcon style={{ width: 12, height: 12 }} />
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default NotificationPanel;
